// Every window of the game screen and how they open, close and sit together: stations with the inventory beside them, the picked-up stack, confirm prompts, right-click menus.
import { app } from '../app';
import { api, itemsById, content, Container } from '../api';
import { Widget, type UiPointerEvent, widget, uiScale, characterIcon, itemIcon } from '../ui/compose';
import { hideTip } from '../ui/tooltips';
import { settings, saveSettings, CHAT_TEXT_SIZES, CHAT_BACKGROUNDS } from '../settings';
import type { GameScreen } from './gameScreen';
import type { Frame, GameEvent } from './types';
import type { HudMenu } from './hud';
import { InventoryWindow, CharacterWindow, CraftingWindow, BagWindow, VendorWindow, TradeWindow, button, type Slot, type Stack } from './items';
import { MapChunks, Minimap } from './minimap';
import { TalentWindow } from './talents';
import { DialogueWindow, QuestJournal, QuestTracker, PartyFrame, Invites, ContextMenu, FriendsWindow, IgnoreWindow, AdminPanel, WorldMap, SettingsWindow, type MenuItem, type SocialData } from './people';

interface Confirm { title: string; color: string; lines: string[]; yes: string; no: string; onYes: () => void; stillValid: () => boolean }

export class Windows {
  readonly root = widget('box', { class: 'windows passthrough' });
  private readonly row = widget('box', { class: 'window-row' });
  readonly inventory = new InventoryWindow(this);
  readonly character = new CharacterWindow(this);
  readonly crafting = new CraftingWindow(this);
  readonly bag = new BagWindow(this);
  readonly vendor = new VendorWindow(this);
  readonly trade = new TradeWindow(this);
  readonly dialogue = new DialogueWindow(this);
  readonly tracker = new QuestTracker(this);
  readonly journal = new QuestJournal(this);
  readonly party = new PartyFrame(this);
  readonly invites = new Invites();
  readonly context = new ContextMenu();
  readonly friends = new FriendsWindow(this);
  readonly ignores = new IgnoreWindow(this);
  readonly admin = new AdminPanel(this);
  readonly talents = new TalentWindow(this);
  /** Explored chunks, shared by the world map and the minimap. */
  readonly mapChunks = new MapChunks();
  readonly map = new WorldMap(this.mapChunks);
  readonly minimap = new Minimap(this.mapChunks, () => this.map.toggle());
  /** The top right corner: the minimap, and the quest tracker under it. */
  private readonly rightStack = widget('box', { class: 'hud-right-stack' }, this.minimap.el, this.tracker.el);
  readonly settings = new SettingsWindow();
  held: Slot | null = null;
  private readonly heldEl = widget('box', { class: 'held' });
  private confirm: Confirm | null = null;
  private confirmEl: Widget | null = null;
  private dirty = true;
  private rev = -1;
  private tradeShown = false;
  private mapTimer = 0;
  private mapZone = -1;
  private social: SocialData | null = null;
  private frame: Frame | null = null;

  constructor(readonly game: GameScreen) {
    window.addEventListener('mousemove', this.moveHeld);
    this.root.addEventListener('mousedown', e => {
      // Clicks on a window's background are the window's (never a world click).
      if ((e.target as Widget).closest('.window')) e.stopPropagation();
    });
  }

  mount(ui: Widget): void {
    this.root.append(this.row, this.dialogue.el, this.rightStack, this.party.el, this.invites.el, this.friends.el, this.ignores.el, this.journal.el, this.admin.el, this.map.el, this.talents.el);
    ui.append(this.root);
    app.ui.overlay.append(this.settings.el, this.heldEl);
    this.admin.render();
    this.settings.render();
  }

  private moveHeld = (e: MouseEvent) => {
    this.heldEl.style.left = `${e.clientX / uiScale}px`;
    this.heldEl.style.top = `${e.clientY / uiScale}px`;
  };
  dispose(): void { window.removeEventListener('mousemove', this.moveHeld); this.map.dispose(); this.talents.dispose(); this.context.close(); }

  get confirming(): boolean { return this.confirm !== null; }
  get openBag(): number { return this.bag.bag; }

  refresh(): void { this.dirty = true; }

  face(appearance: number, size: number): Widget { return characterIcon(appearance, size); }

  // ===========================================================================================================
  // opening and closing
  // ===========================================================================================================

  private get stationUp(): boolean { return this.crafting.isOpen || this.bag.isOpen || this.dialogue.isOpen || this.vendor.isOpen; }

  stationOpen(): boolean { return this.stationUp; }

  anyOpen(): boolean {
    return this.inventory.isOpen || this.character.isOpen || this.stationUp || this.trade.open || this.map.isOpen || this.friends.isOpen || this.ignores.isOpen || this.journal.isOpen || this.settings.isOpen || this.talents.isOpen;
  }

  blocksInteraction(): boolean { return this.anyOpen(); }

  capturesWheel(): boolean { return this.crafting.isOpen || this.map.isOpen || this.talents.isOpen; }

  /** Closes any station (workbench, furnace, bag, shop, dialogue) together with the inventory beside it. */
  closeStation(): boolean {
    if (!this.stationUp) return false;
    this.crafting.close();
    this.bag.close();
    this.vendor.close();
    this.dialogue.close();
    this.closeInventory();
    return true;
  }

  closeInventory(): void {
    this.inventory.isOpen = false;
    this.inventory.shiftTarget = null;
    this.inventory.shiftHint = null;
    this.setHeld(null);
    this.dismissConfirm();
    this.dirty = true;
  }

  closeCharacter(): void { this.character.isOpen = false; this.dirty = true; }

  closeAll(): void {
    this.closeStation();
    this.closeInventory();
    this.character.isOpen = false;
    this.map.close();
    this.talents.close();
    this.context.close();
    this.friends.close();
    this.ignores.close();
    this.journal.close();
  }

  /** Esc: the innermost thing closes. Returns false when nothing did (the pause menu toggles). */
  escape(): boolean {
    if (this.confirm) { this.dismissConfirm(); return true; }
    if (this.map.isOpen) { this.map.close(); return true; }
    if (this.talents.isOpen) { this.talents.close(); return true; }
    if (this.context.isOpen) { this.context.close(); return true; }
    if (this.settings.isOpen) { this.settings.close(); return true; }
    if (this.friends.isOpen) { this.friends.close(); this.dirty = true; return true; }
    if (this.ignores.isOpen) { this.ignores.close(); this.dirty = true; return true; }
    if (this.journal.isOpen) { this.journal.close(); this.dirty = true; return true; }
    if (this.admin.placing !== 'none') { this.admin.cancelPlacing(); return true; }
    if (this.trade.open) { api.tradeCancel(); return true; } // the server closes it for both
    if (this.closeStation()) return true;
    if (this.inventory.isOpen || this.character.isOpen) { this.closeInventory(); this.closeCharacter(); return true; }
    return false;
  }

  openSettings(): void { this.settings.toggle(); }
  toggleIgnores(): void { this.friends.close(); this.ignores.toggle(); this.dirty = true; }
  toggleFriends(): void { this.ignores.close(); this.friends.toggle(); this.dirty = true; }
  toggleTalents(): void {
    this.context.close();
    this.talents.toggle();
    app.audio.play('sfx_ui_open', 0.5, 0, 0, this.talents.isOpen ? 0 : -0.3);
  }

  /** A HUD menu button: the same as its hotkey. */
  menu(m: HudMenu): void {
    if (m === 'settings') { this.openSettings(); return; }
    const code = { bag: 'KeyB', character: 'KeyC', quests: 'KeyJ', friends: 'KeyO', map: 'KeyM', talents: 'KeyN' }[m];
    this.key(new KeyboardEvent('keydown', { code }));
  }

  /** Hotkeys (the game screen passes keys that aren't typing). */
  key(e: KeyboardEvent): void {
    const uiOpen = (sound: boolean, open: boolean) => { if (sound) app.audio.play('sfx_ui_open', 0.5, 0, 0, open ? 0 : -0.3); };
    switch (e.code) {
      case 'KeyM':
        this.context.close();
        this.map.toggle();
        break;
      case 'KeyC':
        if (this.map.isOpen) { this.map.recenter(); break; }
        if (this.stationUp || this.trade.open) break;
        this.character.isOpen = !this.character.isOpen;
        uiOpen(true, this.character.isOpen);
        this.dirty = true;
        break;
      case 'KeyB':
        if (this.map.isOpen) break;
        if (this.trade.open) api.tradeCancel();
        else if (this.crafting.isOpen || this.bag.isOpen || this.vendor.isOpen) this.closeStation();
        else {
          if (this.inventory.isOpen) this.closeInventory(); else this.inventory.isOpen = true;
          uiOpen(true, this.inventory.isOpen);
        }
        this.dirty = true;
        break;
      case 'KeyN':
        if (this.map.isOpen) break;
        this.toggleTalents();
        break;
      case 'KeyO':
        this.context.close();
        this.toggleFriends();
        uiOpen(true, this.friends.isOpen);
        break;
      case 'KeyJ':
        if (this.map.isOpen) break;
        this.context.close();
        this.journal.toggle();
        uiOpen(true, this.journal.isOpen);
        this.dirty = true;
        break;
      case 'KeyQ':
        if (this.inventory.isOpen) this.inventory.dropHovered(e.ctrlKey);
        break;
      case 'F5':
        e.preventDefault();
        if (this.frame?.hud.admin || this.admin.isOpen) this.admin.toggle();
        else api.notice('error', 'Admin tools are only available to server admins.');
        break;
    }
  }

  /** The server said E opened something (WebGame's "open" event). */
  open(e: GameEvent): void {
    this.closeStation();
    switch (e.what) {
      case 'crafting': // a workbench or furnace: its recipes
        this.crafting.open(e.id);
        this.inventory.isOpen = true;
        app.audio.play('sfx_ui_open', 0.6, 0);
        break;
      case 'vendor':
        this.vendor.open(e.id);
        this.inventory.isOpen = true;
        this.inventory.shiftTarget = (c, i) => this.vendor.sell(c, i);
        this.inventory.shiftHint = s => this.vendor.sellHint(s);
        app.audio.play('sfx_ui_open', 0.6, 0);
        break;
      case 'npc':
        this.dialogue.open(e.id);
        app.audio.play('sfx_ui_open', 0.6, 0);
        break;
      case 'bag': {
        const id = e.id;
        this.bag.open(id);
        this.inventory.isOpen = true;
        this.inventory.shiftTarget = (c, i) => api.bagDeposit(id, c, i);
        app.audio.play('sfx_bag_open', 0.8, 0.05);
        break;
      }
    }
    this.character.isOpen = false;
    this.dirty = true;
  }

  // ===========================================================================================================
  // the picked-up stack, moves that bind, drops
  // ===========================================================================================================

  setHeld(slot: Slot | null): void {
    this.held = slot;
    this.heldEl.replaceChildren();
    if (slot) {
      const s = this.inventory.stack(slot.c, slot.i);
      if (s && s.count > 0) this.heldEl.append(itemIcon(s.item, 16));
      hideTip();
    }
    this.dirty = true;
  }

  requestMove(fromC: number, from: number, toC: number, to: number): void {
    const binds = api.moveBinds(fromC, from, toC, to);
    const a = this.inventory.stack(fromC, from), b = this.inventory.stack(toC, to);
    if (binds) this.askToEquip(binds, () => api.moveItem(fromC, from, toC, to),
      () => same(this.inventory.stack(fromC, from), a) && same(this.inventory.stack(toC, to), b));
    else api.moveItem(fromC, from, toC, to);
  }

  requestQuickMove(c: number, i: number): void {
    const binds = api.quickMoveBinds(c, i), s = this.inventory.stack(c, i);
    if (binds) this.askToEquip(binds, () => api.quickMove(c, i), () => same(this.inventory.stack(c, i), s));
    else api.quickMove(c, i);
  }

  private askToEquip(item: number, equip: () => void, stillValid: () => boolean): void {
    const def = itemsById.get(item);
    this.showConfirm({ title: `Equip ${def?.name ?? 'it'}?`, color: def?.qualityColor ?? '#fff',
      lines: ['It will become soulbound to you: you won\'t be', 'able to drop it, only destroy it.'], yes: 'Equip', no: 'Cancel', onYes: equip, stillValid });
  }

  /** Drops a stack in the world, or, if it's soulbound, asks to destroy it instead. */
  dropOrDestroy(c: number, i: number, count: number): void {
    const s = this.inventory.stack(c, i);
    if (!s || s.count <= 0) return;
    if (s.bound) {
      const def = itemsById.get(s.item);
      this.showConfirm({ title: `Destroy ${def?.name ?? 'it'}${s.count > 1 ? ` x${s.count}` : ''}?`, color: def?.qualityColor ?? '#fff',
        lines: ['It\'s soulbound to you, so it can\'t be dropped.', 'Destroying it can\'t be undone.'], yes: 'Destroy', no: 'Keep',
        onYes: () => api.destroyItem(c, i, s.item), stillValid: () => same(this.inventory.stack(c, i), s) });
      return;
    }
    api.dropItem(c, i, count);
    app.audio.play('sfx_drop', 0.7, 0.08);
  }

  /** A click on the world with a stack held drops it (true: the click was used). */
  canvasClick(): boolean {
    if (this.confirm) return true;
    if (!this.held) return false;
    const h0 = this.held;
    this.setHeld(null);
    this.dropOrDestroy(h0.c, h0.i, this.inventory.stack(h0.c, h0.i)?.count ?? 1);
    return true;
  }

  showConfirm(c: Confirm): void {
    this.dismissConfirm();
    this.confirm = c;
    this.confirmEl = widget('box', { class: 'screen confirm', style: { background: 'rgba(0,0,0,0.35)', zIndex: '50' }, onmousedown: (e: UiPointerEvent) => e.stopPropagation() },
      widget('box', { class: 'panel col', style: { alignItems: 'center', padding: `6px`, minWidth: `200px` } },
        widget('box', { style: { color: c.color } }, c.title), ...c.lines.map(l => widget('box', { class: 'small dim' }, l)),
        widget('box', { class: 'buttons' }, button(c.yes, () => { c.onYes(); this.dismissConfirm(); }), button(c.no, () => this.dismissConfirm()))));
    app.ui.overlay.append(this.confirmEl);
  }

  private dismissConfirm(): void {
    this.confirm = null;
    this.confirmEl?.dispose();
    this.confirmEl = null;
  }

  // ===========================================================================================================
  // right-click menus
  // ===========================================================================================================

  private canInvite(name: string): boolean {
    const s = this.social;
    if (!s) return false;
    const inParty = s.party.some(m => m.name.toLowerCase() === name.toLowerCase());
    return !inParty && (s.party.length === 0 || s.isLeader) && s.party.length < s.partyMax;
  }

  private ignoreItem(name: string): MenuItem {
    const ignored = this.social?.ignored.some(n => n.toLowerCase() === name.toLowerCase());
    return ignored ? { text: 'Unignore', action: () => api.ignore(name, false) } : { text: 'Ignore', action: () => api.ignore(name, true) };
  }

  private isFriend(name: string): boolean {
    return !!this.social?.friends.some(f => f.name.toLowerCase() === name.toLowerCase());
  }

  private friendItem(name: string): MenuItem {
    return this.isFriend(name) ? { text: 'Remove friend', action: () => api.friend(name, false) } : { text: 'Add friend', action: () => api.friend(name, true) };
  }

  private leaderItems(name: string, online = true): MenuItem[] {
    if (!this.social?.isLeader) return [];
    return [{ text: 'Make leader', action: () => api.partyAction(5, name), enabled: online }, { text: 'Remove from party', action: () => api.partyAction(4, name) }];
  }

  /** Right-click on the world: a player under the cursor (nearest feet wins). */
  /** A player's menu, if one is under the point (true when it opened). */
  worldMenu(w: { x: number; y: number }, x: number, y: number): boolean {
    const f = this.frame;
    if (!f) return false;
    let target: Frame['ents'][0] | null = null, best = Infinity;
    for (const e of f.ents) {
      if (e.mob || w.x < e.x - 6 || w.x > e.x + 6 || w.y < e.y - 16 || w.y > e.y + 1) continue;
      const d = (w.x - e.x) ** 2 + (w.y - e.y) ** 2;
      if (d < best) { best = d; target = e; }
    }
    if (!target) { this.context.close(); return false; }
    const name = target.name!;
    const inParty = !!this.social?.party.some(m => m.name.toLowerCase() === name.toLowerCase());
    const items: MenuItem[] = [];
    if (!inParty) items.push({ text: 'Invite to party', action: () => api.partyAction(0, name), enabled: this.canInvite(name) });
    const near = Math.hypot(target.x - f.me.x, target.y - f.me.y) <= 6 * 16;
    items.push({ text: 'Trade', action: () => api.tradeRequest(name), enabled: !this.trade.open && near });
    items.push({ text: 'Whisper', action: () => this.game.openChat(`/w ${name} `) });
    if (inParty) items.push(...this.leaderItems(name));
    items.push(this.friendItem(name));
    items.push(this.ignoreItem(name));
    this.context.open(name, x, y, items);
    return true;
  }

  chatNameMenu(name: string, x: number, y: number): void {
    if (name.toLowerCase() === this.frame?.me.name.toLowerCase()) return;
    const items: MenuItem[] = [{ text: 'Whisper', action: () => this.game.openChat(`/w ${name} `) }];
    if (!this.social?.party.some(m => m.name.toLowerCase() === name.toLowerCase())) items.push({ text: 'Invite to party', action: () => api.partyAction(0, name), enabled: this.canInvite(name) });
    items.push(this.friendItem(name));
    items.push(this.ignoreItem(name));
    this.context.open(name, x, y, items);
  }

  /** The chat's cog: text size, the band behind lines, speech bubbles and the filter. Stays open while choosing. */
  chatSettingsMenu(x: number, y: number): void {
    const pick = (change: () => void) => () => { change(); saveSettings(); this.chatSettingsMenu(x, y); };
    const mark = (on: boolean, text: string) => `${on ? '>' : '  '} ${text}`;
    this.context.open('Chat', x, y, [
      ...CHAT_TEXT_SIZES.map((size, i) => ({ text: mark(settings.chatTextSize === i, `Text: ${size.name}`), action: pick(() => settings.chatTextSize = i) })),
      ...CHAT_BACKGROUNDS.map((band, i) => ({ text: mark(settings.chatBackground === i, `Background: ${band.name}`), action: pick(() => settings.chatBackground = i) })),
      { text: mark(settings.chatBubbles, 'Speech bubbles'), action: pick(() => settings.chatBubbles = !settings.chatBubbles) },
      { text: mark(settings.profanityFilter, 'Profanity filter'), action: pick(() => settings.profanityFilter = !settings.profanityFilter) },
    ]);
  }

  partyMenu(m: SocialData['party'][0], s: SocialData, x: number, y: number): void {
    const isMe = m.name.toLowerCase() === s.me.toLowerCase();
    const items: MenuItem[] = isMe ? [{ text: 'Leave party', action: () => api.partyAction(3, '') }]
      : [{ text: 'Whisper', action: () => this.game.openChat(`/w ${m.name} `), enabled: m.online }, ...this.leaderItems(m.name, m.online), this.friendItem(m.name)];
    this.context.open(m.online ? m.name : `${m.name} (offline)`, x, y, items);
  }

  friendMenu(f: SocialData['friends'][0], x: number, y: number): void {
    const inParty = !!this.social?.party.some(m => m.name.toLowerCase() === f.name.toLowerCase());
    const items: MenuItem[] = [{ text: 'Whisper', action: () => this.game.openChat(`/w ${f.name} `), enabled: f.online }];
    if (!inParty) items.push({ text: 'Invite to party', action: () => api.partyAction(0, f.name), enabled: f.online && this.canInvite(f.name) });
    items.push({ text: 'Remove friend', action: () => api.friend(f.name, false) });
    this.context.open(f.online ? f.name : `${f.name} (offline)`, x, y, items);
  }

  questMenu(quest: number, title: string, x: number, y: number): void {
    this.context.open(title, x, y, [{ text: 'Open in journal', action: () => { this.journal.open(quest); this.dirty = true; } },
      { text: 'Abandon quest...', action: () => this.abandonQuest(quest, title, x, y) }]);
  }

  /** Asks before abandoning a quest. */
  abandonQuest(quest: number, title: string, x: number, y: number): void {
    this.context.open(`Abandon ${title}?`, x, y, [
      { text: 'Abandon (progress is lost)', action: () => api.questAction(quest, 2) }, { text: 'Keep it', action: () => { } }]);
  }

  // ===========================================================================================================
  // per frame
  // ===========================================================================================================

  onRevision(_f: Frame): void { this.dirty = true; }

  update(f: Frame, dt: number): void {
    this.frame = f;
    this.admin.setPlayer(f.me.x, f.me.y, f.me.f, f.hud.god);
    if (this.admin.isOpen && !f.hud.admin) this.admin.toggle(); // rights revoked while open
    if (f.rev !== this.rev) { this.rev = f.rev; this.dirty = true; }
    // Explored chunks, every half second: the minimap shows them, and the world map while open.
    this.mapTimer -= dt;
    if (this.mapTimer <= 0 || f.zoneSerial !== this.mapZone) {
      this.mapTimer = 0.5;
      this.mapZone = f.zoneSerial;
      this.mapChunks.refresh();
      this.map.draw();
    }
    this.minimap.el.style.display = this.map.isOpen ? 'none' : '';
    if (!this.map.isOpen) this.minimap.draw(f, f.zone.clock);
    // Held stack: a sync may have emptied it.
    if (this.held && !(this.inventory.stack(this.held.c, this.held.i)?.count)) this.setHeld(null);
    if (this.confirm && !this.confirm.stillValid()) this.dismissConfirm();
    if (this.inventory.isOpen) this.inventory.tick(dt);
    if (!this.dirty) return;
    this.dirty = false;

    this.social = api.social() as SocialData;
    this.party.render(this.social, f.hud.hp / Math.max(1, f.hud.maxHp), f.zone.name);
    this.invites.render(this.social);
    this.friends.render(this.social);
    this.ignores.render(this.social);
    this.tracker.render();
    this.journal.render();
    this.talents.render();
    this.admin.refresh();

    // A trade opened (or ended): the inventory opens beside it, and shift-click offers.
    const tradeOpen = this.trade.render();
    if (tradeOpen && !this.tradeShown) {
      this.tradeShown = true;
      this.closeStation();
      this.character.isOpen = false;
      this.inventory.isOpen = true;
      this.inventory.shiftTarget = (c, i) => api.tradeOffer(c, i);
      this.inventory.shiftHint = s => this.trade.offerHint(s);
      app.audio.play('sfx_ui_open', 0.6, 0);
    } else if (!tradeOpen && this.tradeShown) {
      this.tradeShown = false;
      this.closeInventory();
    }

    // Stations close when they've gone or the player walked away.
    if (this.crafting.isOpen && !this.crafting.render()) this.closeStation();
    if (this.bag.isOpen && !this.bag.render()) this.closeStation();
    if (this.vendor.isOpen && !this.vendor.render()) this.closeStation();
    if (this.dialogue.isOpen && !this.dialogue.render()) this.closeStation();
    this.dialogue.el.style.display = this.dialogue.isOpen ? this.dialogue.el.style.display : 'none';
    if (this.inventory.isOpen) this.inventory.render();
    if (this.character.isOpen) this.character.render();

    // Side by side: the station, trade or character sheet on the left, the inventory on the right.
    const left = this.crafting.isOpen ? this.crafting.el : this.bag.isOpen ? this.bag.el : this.vendor.isOpen ? this.vendor.el
      : this.trade.open ? this.trade.el : this.character.isOpen ? this.character.el : null;
    const children = [left, this.inventory.isOpen ? this.inventory.el : null].filter((x): x is Widget => !!x);
    if (children.length !== this.row.widgets.length || children.some((c, i) => this.row.widgets[i] !== c)) this.row.replaceChildren(...children);
    if (!children.length) hideTip();
  }
}

function same(a: Stack | null, b: Stack | null): boolean {
  return !!a && !!b && a.item === b.item && a.count === b.count && a.bound === b.bound;
}

export { Container, content };
